// Meeting (sestanek): one long recording (pause/resume), transcribed with speaker diarization.
// Transcript editing, speaker names, AI minutes, Word export, continuation recordings.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Minutes, Speaker } from '../../../shared/types';
import {
  addAudioClip,
  deleteDocument,
  generateMinutes,
  getLatestDocument,
  retranscribeClip,
  updateClip,
  updateDocument,
  updateSegment,
  type ViewClip,
  type ViewDocument,
} from '../lib/api';
import { defaultSpeakerName, formatDuration, formatLongDate, formatTime } from '../lib/format';
import { useDocument, useFeatures, useOutboxStatus, useRecorder } from '../lib/hooks';
import { flush, opsForDocument } from '../lib/outbox';
import { discardRecording, type RecordingResult } from '../lib/recorder';
import { navigate } from '../lib/router';
import { copyText, documentToText, exportFileName, meetingToDocx, saveFile } from '../export';
import { AudioPlayer, type SeekRequest } from '../components/AudioPlayer';
import { ClipStatusCard } from '../components/ClipStatusCard';
import { RecordingKeptCard, useContentHidden } from '../components/ContentHidden';
import { confirmDialog } from '../components/Dialog';
import { TitleInput } from '../components/EditableText';
import {
  errorText,
  exportPlaceholders,
  isAbortError,
  maxSeq,
  newId,
  routeForDocument,
  useLatest,
  warnNotPersisted,
} from '../components/helpers';
import { Icon } from '../components/Icon';
import { LevelMeter } from '../components/LevelMeter';
import { Menu, type MenuItem } from '../components/Menu';
import { ProjectPicker } from '../components/ProjectPicker';
import { MinutesCard } from '../components/MinutesCard';
import { Link, useLeaveGuard } from '../components/NavGuard';
import { RecordButton } from '../components/RecordButton';
import { MutedNotice, RecorderAnnouncer, RecorderErrorBox, UnsupportedNotice } from '../components/RecorderNotice';
import { mergeSpeakers, nextSpeakerKey, SpeakerChips, speakersInUse } from '../components/SpeakerEditor';
import { toast } from '../components/Toasts';
import { ClipTranscript, segmentAt } from '../components/Transcript';
import { DocumentMissing, PageLoading } from './NotFound';

const SPEAKER_COUNTS = [2, 3, 4, 5, 6, 7, 8, 9, 10];
const ACCEPT_UPLOAD = 'audio/*,video/mp4,video/webm';
const DURATION_PROBE_TIMEOUT_MS = 8_000;

const EXTENSION_MIME: Record<string, string> = {
  webm: 'audio/webm',
  weba: 'audio/webm',
  ogg: 'audio/ogg',
  oga: 'audio/ogg',
  opus: 'audio/ogg',
  m4a: 'audio/mp4',
  mp4: 'video/mp4',
  aac: 'audio/aac',
  mp3: 'audio/mpeg',
  wav: 'audio/wav',
  flac: 'audio/flac',
};

/** MIME type of a picked file; some Android pickers report '' for .m4a and friends. */
export function uploadMimeType(file: { name: string; type: string }): string | null {
  const type = file.type.toLowerCase();
  if (type.startsWith('audio/') || type === 'video/mp4' || type === 'video/webm') return file.type;
  if (type) return null;
  const ext = /\.([a-z0-9]+)$/i.exec(file.name)?.[1]?.toLowerCase();
  return (ext && EXTENSION_MIME[ext]) || null;
}

/** Duration via an off-DOM <audio> element; 0 when the browser cannot tell (e.g. some webm files). */
function probeDuration(file: Blob): Promise<number> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const el = document.createElement('audio');
    let settled = false;
    const finish = (ms: number) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      el.removeAttribute('src');
      URL.revokeObjectURL(url);
      resolve(ms);
    };
    const timer = setTimeout(() => finish(0), DURATION_PROBE_TIMEOUT_MS);
    el.preload = 'metadata';
    el.addEventListener('loadedmetadata', () => finish(Number.isFinite(el.duration) && el.duration > 0 ? Math.round(el.duration * 1000) : 0));
    el.addEventListener('error', () => finish(0));
    el.src = url;
  });
}

function speakerNumber(key: string): number {
  const n = /^S(\d+)$/.exec(key)?.[1];
  return n ? Number(n) : Number.MAX_SAFE_INTEGER;
}

/**
 * `base` with the pending (not yet loaded) speaker names applied; with `addMissing`, pending
 * speakers that `base` does not know yet are added too.
 */
export function withPendingSpeakers(base: Speaker[], pending: readonly Speaker[], addMissing: boolean): Speaker[] {
  if (pending.length === 0) return base;
  const names = new Map(pending.map((p) => [p.key, p.name]));
  const list = base.map((s) => (names.has(s.key) ? { ...s, name: names.get(s.key)! } : s));
  if (addMissing) {
    for (const p of pending) if (!list.some((s) => s.key === p.key)) list.push({ key: p.key, name: p.name });
    list.sort((a, b) => speakerNumber(a.key) - speakerNumber(b.key) || a.key.localeCompare(b.key));
  }
  return list;
}

function newerMinutes(a: Minutes | null, b: Minutes | null): Minutes | null {
  if (!a) return b;
  if (!b) return a;
  return b.generatedAt > a.generatedAt ? b : a;
}

/** Rejection handler for queued edits (they only fail when local storage fails). */
const reportError = (fallback: string) => (err: unknown) => {
  toast(errorText(err, fallback), { kind: 'error' });
};

interface SessionInfo {
  clipId: string;
  seq: number;
  speakerCount?: number;
}

export function MeetingPage({ id }: { id: string }) {
  const { data, loading, error, stale, reload } = useDocument(id);
  // A running recording keeps the view (and its recorder) mounted even when the document
  // disappears meanwhile (deleted on another device): stopping it then keeps the audio for recovery.
  const [recordingActive, setRecordingActive] = useState(false);
  const lastDoc = useRef<ViewDocument | null>(null);
  useEffect(() => {
    if (data) lastDoc.current = data;
  }, [data]);
  const doc = data ?? (recordingActive ? lastDoc.current : null);

  useEffect(() => {
    if (data && data.type !== 'meeting') navigate(routeForDocument(data), { replace: true });
  }, [data]);

  if (!doc) {
    if (loading) return <PageLoading />;
    return <DocumentMissing error={error} onRetry={reload} />;
  }
  if (doc.type !== 'meeting') return <PageLoading />;
  return <MeetingView doc={doc} stale={stale} missing={!data} onRecordingChange={setRecordingActive} />;
}

interface MeetingViewProps {
  doc: ViewDocument;
  stale: boolean;
  /** The document no longer loads (deleted elsewhere); shown only while a recording runs. */
  missing: boolean;
  onRecordingChange: (recording: boolean) => void;
}

function MeetingView({ doc, stale, missing, onRecordingChange }: MeetingViewProps) {
  const docId = doc.id;
  const rec = useRecorder('meeting');
  const hidden = useContentHidden();
  const features = useFeatures().data;
  const online = useOutboxStatus().online;

  const [speakerCount, setSpeakerCount] = useState<number | undefined>(undefined);
  const [session, setSession] = useState<SessionInfo | null>(null);
  const sessionRef = useRef<SessionInfo | null>(null);
  const startPromise = useRef<Promise<void> | null>(null);
  const finishing = useRef<Promise<boolean> | null>(null);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<'export' | 'minutes' | 'upload' | 'copy' | null>(null);
  const [localMinutes, setLocalMinutes] = useState<Minutes | null>(null);
  const lastSeq = useRef(0);
  const fileInput = useRef<HTMLInputElement>(null);

  const [activeClipId, setActiveClipId] = useState<string | null>(null);
  const [seek, setSeek] = useState<SeekRequest | null>(null);
  const [activeSegmentId, setActiveSegmentId] = useState<string | null>(null);
  const seekNonce = useRef(0);

  // Renames and new speakers that the loaded document does not show yet (its reload can take
  // seconds on a weak connection). Every PATCH sends the whole list, so it is built with these.
  const [pendingSpeakers, setPendingSpeakers] = useState<readonly Speaker[]>([]);
  const pendingSpeakersRef = useRef<readonly Speaker[]>([]);
  const setPending = useCallback((next: readonly Speaker[]) => {
    pendingSpeakersRef.current = next;
    setPendingSpeakers(next);
  }, []);
  const baseSpeakers = useMemo(() => mergeSpeakers(doc), [doc]);
  const baseSpeakersRef = useLatest(baseSpeakers);
  const speakers = useMemo(() => withPendingSpeakers(baseSpeakers, pendingSpeakers, true), [baseSpeakers, pendingSpeakers]);
  const shownSpeakers = useMemo(() => withPendingSpeakers(speakersInUse(doc), pendingSpeakers, false), [doc, pendingSpeakers]);
  /** The speaker list as it will be once every pending change is stored. */
  const currentSpeakers = useCallback(
    () => withPendingSpeakers(baseSpeakersRef.current, pendingSpeakersRef.current, true),
    [baseSpeakersRef],
  );
  const playable = useMemo(() => doc.clips.filter((c) => c.hasAudio), [doc]);
  const activeClip = playable.find((c) => c.id === activeClipId) ?? playable[0] ?? null;
  const activeClipRef = useLatest(activeClip);
  const minutes = newerMinutes(doc.minutes, localMinutes);
  const recording = session !== null;
  const paused = rec.state === 'paused';
  const stopping = rec.state === 'stopping' || saving;
  const unfinished = doc.clips.some((c) => c.localPending || c.status !== 'done');
  const hasText = doc.clips.some((c) => c.status === 'done' && c.text.trim() !== '');

  const nextSeq = () => Math.max(maxSeq(doc.clips), lastSeq.current) + 1;

  useEffect(() => {
    onRecordingChange(recording);
  }, [recording, onRecordingChange]);

  // Pending speaker changes are dropped once the loaded document shows them, or once nothing is
  // queued any more although it does not (the server refused them or they were replaced elsewhere).
  useEffect(() => {
    const pending = pendingSpeakersRef.current;
    if (pending.length === 0) return;
    const left = pending.filter((p) => !doc.speakers.some((s) => s.key === p.key && s.name === p.name));
    if (left.length !== pending.length) setPending(left);
    if (left.length === 0) return;
    let cancelled = false;
    opsForDocument(docId)
      .then((ops) => {
        if (!cancelled && !ops.some((o) => o.kind === 'updateDocument')) setPending([]);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [doc, docId, setPending]);

  // ---------- recording ----------

  const clearSession = () => {
    sessionRef.current = null;
    setSession(null);
  };

  /** Must be called directly from the click: the recorder needs the user gesture (iOS). */
  const startRecording = () => {
    if (sessionRef.current || !rec.supported) return;
    const info: SessionInfo = { clipId: newId(), seq: nextSeq() };
    if (speakerCount) info.speakerCount = speakerCount;
    sessionRef.current = info;
    setSession(info);
    setNotice(null);
    // Stored with the crash-recovery data, so a recovered recording lands in the right place.
    const recoveryMeta: Record<string, unknown> = {
      docId,
      clipId: info.clipId,
      type: 'meeting',
      title: doc.title,
      seq: info.seq,
      diarize: true,
    };
    if (info.speakerCount) recoveryMeta.speakerCount = info.speakerCount;
    const promise = rec.start(recoveryMeta).catch(() => {
      // rec.error holds the message (shown with a retry button).
      if (sessionRef.current === info) clearSession();
    });
    startPromise.current = promise;
    void promise.finally(() => {
      if (startPromise.current === promise) startPromise.current = null;
    });
  };

  const saveAudio = async (result: RecordingResult, info: SessionInfo) => {
    if (result.blob.size === 0) {
      if (result.recoveryId) await discardRecording(result.recoveryId).catch(() => {});
      throw new Error('Nič ni bilo posneto. Preverite mikrofon in poskusite znova.');
    }
    const { persisted } = await addAudioClip(docId, {
      id: info.clipId,
      blob: result.blob,
      mimeType: result.mimeType,
      durationMs: result.durationMs,
      createdAt: result.startedAt,
      seq: info.seq,
      diarize: true,
      ...(info.speakerCount ? { speakerCount: info.speakerCount } : {}),
    });
    lastSeq.current = Math.max(lastSeq.current, info.seq);
    if (!persisted) {
      // Only in memory: its recovery copy (if any) is kept until the server has the audio.
      warnNotPersisted(result.recoveryId !== null ? 'recoverable' : 'lost');
    } else if (result.recoveryId) {
      // The audio is in the outbox now; a leftover recovery copy would only be offered twice.
      await discardRecording(result.recoveryId).catch((err: unknown) => console.warn('[meeting] discard failed', err));
    }
  };

  /** Stops and queues the recording. Resolves true when the audio is safe (outbox or recovery). */
  const finishRecording = (): Promise<boolean> => {
    if (finishing.current) return finishing.current;
    const info = sessionRef.current;
    if (!info) return Promise.resolve(true);
    setSaving(true);
    const promise = (async () => {
      // Leaving while the permission prompt is still open: wait for the start to settle.
      if (startPromise.current) await startPromise.current;
      if (sessionRef.current !== info) return true;
      let result: RecordingResult;
      try {
        result = await rec.stop();
      } catch (err) {
        toast(errorText(err, 'Snemanja ni bilo mogoče ustaviti.'), { kind: 'error' });
        return false;
      }
      try {
        await saveAudio(result, info);
        return true;
      } catch (err) {
        const kept = result.recoveryId !== null && result.blob.size > 0;
        toast(
          `${errorText(err, 'Posnetka ni bilo mogoče shraniti.')}${kept ? ' Posnetek ostaja v brskalniku in bo ponujen za obnovitev.' : ''}`,
          { kind: 'error', durationMs: 0 },
        );
        return kept;
      }
    })().finally(() => {
      finishing.current = null;
      clearSession();
      setSaving(false);
    });
    finishing.current = promise;
    return promise;
  };
  const finishRef = useLatest(finishRecording);

  const cancelRecording = async () => {
    const ok = await confirmDialog({
      title: 'Prekliči snemanje?',
      message: 'Posnetek bo zavržen in ga ne bo mogoče obnoviti.',
      confirmLabel: 'Zavrzi posnetek',
      cancelLabel: 'Nadaljuj snemanje',
      danger: true,
    });
    if (!ok || !sessionRef.current) return;
    // Still opening the microphone: wait, so the recording about to start is the one discarded.
    if (startPromise.current) await startPromise.current;
    if (!sessionRef.current) return;
    try {
      await rec.cancel();
    } catch (err) {
      toast(errorText(err, 'Snemanja ni bilo mogoče preklicati.'), { kind: 'error' });
    } finally {
      clearSession();
    }
  };

  // The system ended the recording (call, unplugged microphone): keep what was recorded.
  useEffect(() => {
    if (!rec.interrupted || !sessionRef.current) return;
    void finishRef.current().then((ok) =>
      setNotice(
        ok
          ? 'Snemanje je bilo prekinjeno (npr. zaradi klica ali odklopa mikrofona). Posneti del je shranjen in bo prepisan. Za nadaljevanje izberite »Snemaj nadaljevanje«.'
          : 'Snemanje je bilo prekinjeno.',
      ),
    );
  }, [rec.interrupted, finishRef]);

  useLeaveGuard(recording, finishRecording);

  // ---------- upload ----------

  const uploadFile = async (file: File) => {
    const mimeType = uploadMimeType(file);
    if (!mimeType) {
      toast('Ta datoteka ni zvočni posnetek. Podprti so npr. m4a, mp3, wav, ogg, webm in mp4.', { kind: 'error' });
      return;
    }
    if (features && file.size > features.maxUploadBytes) {
      toast(`Datoteka je prevelika (največ ${Math.round(features.maxUploadBytes / (1024 * 1024))} MB).`, { kind: 'error' });
      return;
    }
    setBusy('upload');
    try {
      const durationMs = await probeDuration(file);
      const seq = nextSeq();
      const { persisted } = await addAudioClip(docId, {
        id: newId(),
        blob: file,
        mimeType,
        durationMs,
        createdAt: new Date().toISOString(),
        seq,
        diarize: true,
        ...(speakerCount ? { speakerCount } : {}),
      });
      lastSeq.current = Math.max(lastSeq.current, seq);
      if (persisted) {
        toast(online ? 'Posnetek je dodan in se pošilja v prepis.' : 'Posnetek je shranjen in bo poslan, ko bo povezava na voljo.', {
          kind: 'success',
        });
      } else {
        warnNotPersisted('file');
      }
    } catch (err) {
      toast(errorText(err, 'Posnetka ni bilo mogoče dodati.'), { kind: 'error' });
    } finally {
      setBusy(null);
    }
  };

  // ---------- transcript editing (stable callbacks: the transcript is memoised) ----------

  const onSeek = useCallback((clipId: string, ms: number) => {
    setActiveClipId(clipId);
    setSeek({ ms, nonce: ++seekNonce.current });
  }, []);

  const onSaveSegment = useCallback(
    (clipId: string, segmentId: string, text: string) => {
      updateSegment(docId, clipId, segmentId, { text }).catch(reportError('Popravka ni bilo mogoče shraniti.'));
    },
    [docId],
  );

  const onSaveClipText = useCallback(
    (clipId: string, text: string) => {
      updateClip(docId, clipId, { text }).catch(reportError('Popravka ni bilo mogoče shraniti.'));
    },
    [docId],
  );

  const onReassign = useCallback(
    (clipId: string, segmentIds: string[], speakerKey: string | null) => {
      let key = speakerKey;
      if (key === null) {
        key = nextSpeakerKey(currentSpeakers());
        // Store the new speaker so it can be renamed like the others.
        setPending([...pendingSpeakersRef.current, { key, name: defaultSpeakerName(key) }]);
        updateDocument(docId, { speakers: currentSpeakers() }).catch(reportError('Govorca ni bilo mogoče dodati.'));
      }
      for (const segmentId of segmentIds) {
        updateSegment(docId, clipId, segmentId, { speaker: key }).catch(reportError('Govorca ni bilo mogoče spremeniti.'));
      }
    },
    [docId, currentSpeakers, setPending],
  );

  const onRename = useCallback(
    (key: string, name: string) => {
      setPending([...pendingSpeakersRef.current.filter((p) => p.key !== key), { key, name }]);
      updateDocument(docId, { speakers: currentSpeakers() }).catch(reportError('Imena ni bilo mogoče shraniti.'));
    },
    [docId, currentSpeakers, setPending],
  );

  const onTime = useCallback(
    (ms: number) => {
      const clip = activeClipRef.current;
      setActiveSegmentId(clip ? (segmentAt(clip.segments, ms)?.id ?? null) : null);
    },
    [activeClipRef],
  );

  // ---------- actions ----------

  /** The document with an edit this tap just committed (a field saved on blur) and the newest minutes. */
  const latestDoc = async (opts: { network?: boolean } = {}) => {
    const latest = await getLatestDocument(doc, opts);
    return { ...latest, minutes: newerMinutes(latest.minutes, localMinutes) };
  };

  const exportWord = async () => {
    setBusy('export');
    try {
      const full = await latestDoc({ network: true });
      const placeholders = exportPlaceholders(full.clips);
      if (placeholders) {
        const ok = await confirmDialog({
          title: placeholders.pending ? 'Prepis še ni končan' : full.clips.length > 1 ? 'Prepis nekaterih delov ni uspel' : 'Prepis ni uspel',
          message: `${full.clips.length > 1 ? 'Deli brez besedila bodo v dokumentu označeni z' : 'Namesto prepisa bo v dokumentu zapisano'} ${placeholders.labels}. Želite vseeno izvoziti?`,
          confirmLabel: 'Izvozi',
        });
        if (!ok) return;
      }
      const blob = await meetingToDocx(full);
      await saveFile(blob, exportFileName(full, 'docx'), { share: true });
    } catch (err) {
      if (!isAbortError(err)) toast(errorText(err, 'Izvoz v Word ni uspel.'), { kind: 'error' });
    } finally {
      setBusy(null);
    }
  };

  const copy = async () => {
    setBusy('copy');
    try {
      // From this device only: the clipboard needs the tap's user activation (see getLatestDocument).
      const ok = await copyText(documentToText(await latestDoc()));
      toast(ok ? 'Besedilo je kopirano v odložišče.' : 'Brskalnik ni dovolil kopiranja v odložišče.', { kind: ok ? 'success' : 'warning' });
    } catch (err) {
      toast(errorText(err, 'Kopiranje ni uspelo.'), { kind: 'error' });
    } finally {
      setBusy(null);
    }
  };

  const makeMinutes = async () => {
    if (!online) {
      toast('Za pripravo zapisnika je potrebna internetna povezava.', { kind: 'warning' });
      return;
    }
    // The server writes the minutes from its copy: renamed speakers and corrections still waiting
    // in the outbox would be missing from them.
    const queued = await opsForDocument(docId).catch(() => []);
    if (queued.length > 0) {
      flush();
      toast('Spremembe sestanka se še pošiljajo na strežnik. Zapisnik pripravite, ko bodo poslane.', { kind: 'warning' });
      return;
    }
    if (minutes) {
      const ok = await confirmDialog({
        title: 'Pripravi zapisnik znova?',
        message: 'Obstoječi zapisnik bo zamenjan z novim.',
        confirmLabel: 'Pripravi znova',
      });
      if (!ok) return;
    }
    setBusy('minutes');
    try {
      setLocalMinutes(await generateMinutes(docId));
      toast('Zapisnik je pripravljen.', { kind: 'success' });
    } catch (err) {
      toast(errorText(err, 'Zapisnika ni bilo mogoče pripraviti.'), { kind: 'error' });
    } finally {
      setBusy(null);
    }
  };

  const retranscribe = async (clips: ViewClip[]) => {
    const targets = clips.filter((c) => c.hasAudio && !c.localPending);
    if (targets.length === 0) {
      toast('Posnetek še ni na strežniku, zato ga ni mogoče ponovno prepisati.', { kind: 'warning' });
      return;
    }
    if (targets.some((c) => c.edited || c.status === 'done')) {
      const ok = await confirmDialog({
        title: 'Ponovno prepiši?',
        message: 'Besedilo bo ponovno ustvarjeno iz posnetka. Ročni popravki besedila in dodelitve govorcev bodo izgubljeni, imena govorcev ostanejo.',
        confirmLabel: 'Ponovno prepiši',
      });
      if (!ok) return;
    }
    try {
      for (const clip of targets) await retranscribeClip(docId, clip.id);
      toast('Ponovni prepis je v teku.', { kind: 'info' });
    } catch (err) {
      toast(errorText(err, 'Ponovnega prepisa ni bilo mogoče začeti.'), { kind: 'error' });
    }
  };

  const remove = async () => {
    const ok = await confirmDialog({
      title: 'Izbriši sestanek?',
      message: `»${doc.title}« bo izbrisan skupaj s posnetki in prepisom. Tega ni mogoče razveljaviti.`,
      confirmLabel: 'Izbriši',
      danger: true,
    });
    if (!ok) return;
    try {
      await deleteDocument(docId);
      toast('Sestanek je izbrisan.', { kind: 'info' });
      navigate({ name: 'home' }, { replace: true });
    } catch (err) {
      toast(errorText(err, 'Sestanka ni bilo mogoče izbrisati.'), { kind: 'error' });
    }
  };

  const menuItems: MenuItem[] = [];
  if (doc.clips.length > 0 && !recording) {
    menuItems.push({ label: 'Naloži nadaljevanje iz datoteke', icon: 'upload', onSelect: () => fileInput.current?.click() });
    menuItems.push({ label: 'Ponovno prepiši', icon: 'refresh', onSelect: () => void retranscribe(doc.clips) });
  }
  menuItems.push({ label: 'Izbriši sestanek', icon: 'trash', danger: true, separator: menuItems.length > 0, onSelect: () => void remove() });

  // ---------- render ----------

  // Under the login (the session ended): only the running recording, nothing of the meeting.
  if (hidden) return <RecordingKeptCard recording={recording} elapsedMs={rec.elapsedMs} />;

  return (
    <div className="page page--meeting">
      <RecorderAnnouncer state={rec.state} />
      <div className="doc-head">
        <Link to={{ name: 'home' }} className="back-link">
          <Icon name="chevronLeft" size={20} /> Začetek
        </Link>
        <div className="doc-head__row">
          <TitleInput value={doc.title} onSave={(title) => updateDocument(docId, { title }).catch(reportError('Naslova ni bilo mogoče shraniti.'))} ariaLabel="Naslov sestanka" />
          <Menu items={menuItems} label="Več možnosti" />
        </div>
        <p className="doc-head__meta">
          {formatLongDate(doc.createdAt)}, {formatTime(doc.createdAt)}
          {doc.durationMs > 0 && ` · ${formatDuration(doc.durationMs)}`}
        </p>
        <div className="doc-head__project">
          <ProjectPicker doc={doc} noun="sestanka" onChange={(projectId) => updateDocument(docId, { projectId }).catch(reportError('Projekta ni bilo mogoče shraniti.'))} />
        </div>
      </div>

      {missing && (
        <div className="notice notice--warning" role="alert">
          <Icon name="alert" size={20} className="notice__icon" />
          <p className="notice__content">
            Sestanka ni več na strežniku (morda je bil izbrisan v drugi napravi). Snemanje teče naprej; ko ga končate, bo posnetek
            ostal v tej napravi in bo ponujen za obnovitev.
          </p>
        </div>
      )}
      {stale && <p className="muted small offline-hint">Brez povezave – prikazani so podatki, shranjeni v tej napravi.</p>}
      {notice && (
        <div className="notice notice--info" role="status">
          <Icon name="info" size={20} className="notice__icon" />
          <p className="notice__content">{notice}</p>
          <button type="button" className="icon-btn" aria-label="Zapri obvestilo" onClick={() => setNotice(null)}>
            <Icon name="close" size={18} />
          </button>
        </div>
      )}
      {!rec.supported && <UnsupportedNotice alternative="Še vedno lahko naložite obstoječi posnetek iz naprave." />}
      {rec.error && !recording && <RecorderErrorBox message={rec.error} onRetry={rec.supported ? startRecording : undefined} />}

      {recording ? (
        <section className="card rec-panel" aria-label="Snemanje">
          <p className={`rec-status${paused ? ' is-paused' : ''}`}>
            <span className="rec-dot" aria-hidden="true" />
            {rec.state === 'preparing' ? 'Vklapljam mikrofon …' : stopping ? 'Shranjujem …' : paused ? 'Pavza' : 'Snemam'}
          </p>
          <div className="timer" role="timer" aria-label="Trajanje posnetka">
            {formatDuration(rec.elapsedMs)}
          </div>
          <LevelMeter level={rec.level} inactive={paused || rec.state !== 'recording'} />
          <MutedNotice muted={rec.muted} />
          <RecordButton
            recording
            pulse={rec.state === 'recording'}
            label="Končaj in prepiši"
            onClick={() => void finishRecording()}
            disabled={stopping || rec.state === 'preparing'}
            busy={stopping}
          />
          <div className="rec-panel__controls">
            {paused ? (
              <button type="button" className="btn btn--secondary" onClick={rec.resume} disabled={stopping}>
                <Icon name="play" size={20} /> Nadaljuj
              </button>
            ) : (
              <button type="button" className="btn btn--secondary" onClick={rec.pause} disabled={stopping || rec.state !== 'recording'}>
                <Icon name="pause" size={20} /> Pavza
              </button>
            )}
            <button type="button" className="btn btn--ghost" onClick={() => void cancelRecording()} disabled={stopping || rec.state === 'preparing'}>
              Prekliči
            </button>
          </div>
          <p className="hint">
            <Icon name="info" size={18} /> Med snemanjem ne zaklepajte zaslona in ne zapuščajte aplikacije.
          </p>
        </section>
      ) : doc.clips.length === 0 ? (
        <section className="card start-panel" aria-label="Začetek snemanja">
          {rec.supported && (
            <RecordButton recording={false} label="Začni snemanje" onClick={startRecording} busy={rec.state === 'preparing'} />
          )}
          <div className="field field--inline">
            <label htmlFor="speaker-count">Število govorcev</label>
            <select
              id="speaker-count"
              value={speakerCount ?? ''}
              onChange={(e) => setSpeakerCount(e.target.value ? Number(e.target.value) : undefined)}
            >
              <option value="">Samodejno</option>
              {SPEAKER_COUNTS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
          {features && !features.diarization ? (
            <p className="muted small">Trenutni ponudnik prepisa ne razlikuje govorcev – prepis bo brez oznak govorcev.</p>
          ) : (
            <p className="muted small">Namig: telefon položite na sredino mize, čim bližje govorcem.</p>
          )}
          <div className="or-divider">
            <span>ali</span>
          </div>
          <button type="button" className="btn btn--secondary" onClick={() => fileInput.current?.click()} disabled={busy === 'upload'}>
            <Icon name={busy === 'upload' ? 'spinner' : 'upload'} size={20} /> Naloži obstoječi posnetek
          </button>
        </section>
      ) : (
        <div className="toolbar" role="toolbar" aria-label="Dejanja">
          <button type="button" className="btn btn--primary" onClick={() => void exportWord()} disabled={busy === 'export'}>
            <Icon name={busy === 'export' ? 'spinner' : 'file'} size={20} /> Izvozi v Word
          </button>
          <button type="button" className="btn btn--secondary" onClick={() => void copy()} disabled={busy === 'copy'}>
            <Icon name="copy" size={20} /> Kopiraj besedilo
          </button>
          {features?.ai && !minutes && (
            <button
              type="button"
              className="btn btn--secondary"
              onClick={() => void makeMinutes()}
              disabled={busy === 'minutes' || unfinished || !hasText}
              title={unfinished ? 'Na voljo, ko je prepis končan.' : undefined}
            >
              <Icon name={busy === 'minutes' ? 'spinner' : 'sparkles'} size={20} /> {busy === 'minutes' ? 'Pripravljam zapisnik …' : 'Pripravi zapisnik'}
            </button>
          )}
          {rec.supported && (
            <button type="button" className="btn btn--ghost" onClick={startRecording}>
              <Icon name="mic" size={20} /> Snemaj nadaljevanje
            </button>
          )}
        </div>
      )}

      <input
        ref={fileInput}
        type="file"
        accept={ACCEPT_UPLOAD}
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) void uploadFile(file);
        }}
      />

      {shownSpeakers.length > 0 && <SpeakerChips speakers={shownSpeakers} onRename={onRename} />}

      {minutes && (
        <MinutesCard
          minutes={minutes}
          outdated={doc.clips.some((c) => c.localPending || c.updatedAt > minutes.generatedAt)}
          onRegenerate={features?.ai && !unfinished ? () => void makeMinutes() : undefined}
          busy={busy === 'minutes'}
        />
      )}

      {doc.clips.length > 0 && (
        <div className="parts">
          {doc.clips.map((clip, i) => (
            <section key={clip.id} className="part" aria-label={doc.clips.length > 1 ? `Del ${i + 1}` : 'Prepis'}>
              {doc.clips.length > 1 && (
                <div className="part__divider">
                  <span>
                    Del {i + 1} · {formatTime(clip.createdAt)}
                    {clip.durationMs ? ` · ${formatDuration(clip.durationMs)}` : ''}
                  </span>
                </div>
              )}
              {(clip.localPending || clip.status !== 'done') && (
                <ClipStatusCard clip={clip} online={online} onRetry={() => void retranscribe([clip])} />
              )}
              {!clip.localPending && (clip.status === 'done' || clip.text.trim() !== '') && (
                <ClipTranscript
                  clip={clip}
                  speakers={speakers}
                  activeSegmentId={activeClip?.id === clip.id ? activeSegmentId : null}
                  onSeek={onSeek}
                  onSaveSegment={onSaveSegment}
                  onSaveClipText={onSaveClipText}
                  onReassign={onReassign}
                />
              )}
            </section>
          ))}
        </div>
      )}

      {!recording && activeClip && (
        <AudioPlayer clip={activeClip} seek={activeClip.id === activeClipId ? seek : null} onTime={onTime}>
          {playable.length > 1 && (
            <select
              className="player__select"
              aria-label="Del posnetka"
              value={activeClip.id}
              onChange={(e) => {
                setActiveClipId(e.target.value);
                setActiveSegmentId(null);
                setSeek(null);
              }}
            >
              {playable.map((c) => (
                <option key={c.id} value={c.id}>
                  Del {doc.clips.indexOf(c) + 1}
                </option>
              ))}
            </select>
          )}
        </AudioPlayer>
      )}
    </div>
  );
}
